import { createTheme, Theme } from '@mui/material';
import { ErrorColor, Primary, PrimaryText, Success } from './color';
import Secondary from './color/Secondary';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    gradient: true;
  }
}

export const ThemeSpacing = 8; // default recommended value

const LightTheme = createTheme({
  spacing: ThemeSpacing,
  breakpoints: {
    values: {
      xs: 0,
      md: 750,
      lg: 1200,
    },
  },

  palette: {
    primary: {
      main: Primary[100],
      light: Primary[10],
      900: Primary[90],
      800: Primary[80],
      300: Primary[30],
      100: Primary[10],
    },
    primaryText: {
      900: PrimaryText[90],
      800: PrimaryText[80],
      300: PrimaryText[30],
      100: PrimaryText[10],
    },
    secondary: {
      main: '#FFFFFF',
      100: Secondary[10],
      200: Secondary[20],
      contrastText: Primary[100],
    },
    success: {
      main: Success[100],
    },
    error: {
      main: ErrorColor[100],
    },
  },

  typography: {
    fontFamily: ['Arial'].join(','),
    h1: {
      fontSize: 84,
      fontWeight: 800,
      fontFamily: ['Univers LT Pro'].join(','),
    },
    h2: {
      fontSize: 40,
      fontWeight: 600,
      fontFamily: ['Univers LT Pro'].join(','),
    },
    h3: {
      fontSize: 32,
      fontWeight: 200,
      fontFamily: ['Univers LT Pro'].join(','),
    },
    h4: {
      fontSize: 24,
      fontWeight: 800,
      fontFamily: ['Univers LT Pro'].join(','),
    },
    h5: {
      fontSize: 20,
      fontWeight: 800,
      fontFamily: ['Univers LT Pro'].join(','),
    },
    h6: {
      fontSize: 16,
      fontWeight: 800,
      fontFamily: ['Univers LT Pro'].join(','),
    },

    pXSmall: {
      fontSize: 10,
      fontWeight: 400,
    },
    pSmall: {
      fontSize: 13,
      fontWeight: 400,
    },
    pMedium: {
      fontSize: 15,
      fontWeight: 400,
    },
    pLarge: {
      fontSize: 20,
      fontWeight: 400,
    },
    pXLarge: {
      fontSize: 28,
      fontWeight: 400,
    },

    buttonLarge: {
      fontSize: 16,
      fontWeight: 600,
    },
    button: {
      fontSize: 14,
      fontWeight: 600,
    },
    buttonSmall: {
      fontSize: 12,
      fontWeight: 600,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#5692C940',
          backgroundImage: 'linear-gradient(to right, #5692C940 0%, #FFFFAB40 60%)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'transparent',
          borderBottom: '1px solid white',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: 16,
          textTransform: 'none',
          borderRadius: 0,
          height: 56,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'gradient' },
          style: {
            background:
              'linear-gradient(to right bottom, #003A8C 12.5%, #05398B 25%, #143788 37.5%, #2D3484 50%, #50307E 62.5%, #7C2B76 75%, #B2246C 87.5%, #EC1E63 100%)',
            color: 'white',
            textTransform: 'uppercase',
            '&.Mui-disabled': {
              background: 'darkgray',
            },
          },
        },
      ],
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          borderRadius: 0,
          '& fieldset': {
            borderRadius: 0,
            borderColor: '#D6D7D8',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'h6',
          subtitle2: 'h6',
          pXSmall: 'p',
          pSmall: 'p',
          pMedium: 'p',
          pLarge: 'p',
          pXLarge: 'p',
        },
        color: 'primaryText.100',
      },
    },
    MuiAlert: {
      styleOverrides: {
        outlinedError: {
          backgroundColor: ErrorColor[10],
          color: ErrorColor[100],
        },
        outlinedSuccess: {
          backgroundColor: Success[10],
          color: Success[100],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'unset',
        },
      },
    },
  },
});

LightTheme.shadows = Array<string>(25).fill('0px 0px 26px rgba(0, 0, 0, 0.06)') as Theme['shadows'];
LightTheme.shadows.push('0px 0px 26px rgba(0, 0, 0, 0.06)');
LightTheme.shadows.push('3px 0px 26px rgba(0, 0, 0, 0.06)');

declare module '@mui/material/styles' {
  interface TypographyVariants {
    pXSmall: React.CSSProperties;
    pSmall: React.CSSProperties;
    pMedium: React.CSSProperties;
    pLarge: React.CSSProperties;
    pXLarge: React.CSSProperties;

    buttonSmall: React.CSSProperties;
    buttonLarge: React.CSSProperties;
  }

  interface Palette {
    primaryText: Palette['primary'];
  }

  interface PaletteOptions {
    primaryText: PaletteOptions['primary'];
  }

  interface PaletteColor {
    100: string;
    200: string;
    300: string;
    800: string;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    pXSmall?: React.CSSProperties;
    pSmall?: React.CSSProperties;
    pMedium?: React.CSSProperties;
    pLarge?: React.CSSProperties;
    pXLarge?: React.CSSProperties;

    buttonSmall?: React.CSSProperties;
    buttonLarge?: React.CSSProperties;
  }

  interface BreakpointOverrides {
    sm: false;
    xl: false;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pXSmall: true;
    pSmall: true;
    pMedium: true;
    pLarge: true;
    pXLarge: true;

    buttonSmall: true;
    buttonLarge: true;
  }
}

export default LightTheme;
